import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HardHat } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const nav_ = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Welcome back!");
      if (data.subscription_status === "active" || data.role === "admin") nav_("/dashboard");
      else nav_("/pricing");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-white border-r border-slate-200 p-10 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <img src="/quickquote-hero.png" alt="QuickQuote branding" className="w-full h-full object-cover opacity-95" />
        </div>
        <div className="relative flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
            <HardHat className="w-5 h-5 text-white" />
          </div>
          <div className="font-display font-extrabold text-xl text-slate-900">QuickQuote</div>
        </div>
        <div className="relative max-w-md">
          <div className="inline-block bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg px-4 py-3 shadow-sm">
            <div className="text-xs font-bold uppercase tracking-widest text-blue-600">Quick Quotes</div>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed">
              Send quotes in 60 seconds. Get e-signed acceptance, convert to invoices, and get paid — all in one place.
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] font-semibold text-slate-700">
              <div className="bg-slate-50 rounded px-2 py-1.5">60s quotes</div>
              <div className="bg-slate-50 rounded px-2 py-1.5">PDF exports</div>
              <div className="bg-slate-50 rounded px-2 py-1.5">Text &amp; email</div>
            </div>
          </div>
        </div>
        <div className="relative text-xs text-slate-500">© {new Date().getFullYear()} QuickQuote</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
              <HardHat className="w-5 h-5 text-white" />
            </div>
            <div className="font-display font-extrabold text-lg">QuickQuote</div>
          </div>
          <h2 className="font-display text-3xl font-extrabold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Welcome back. Let's build something.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                required
              />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>
          <p className="mt-6 text-sm text-slate-500">
            No account? <Link to="/signup" className="text-blue-600 font-medium hover:underline" data-testid="link-signup">Create one</Link>
            <span className="mx-2 text-slate-300">·</span>
            <Link to="/forgot-password" className="text-blue-600 font-medium hover:underline" data-testid="link-forgot">Forgot password?</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
